import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Phone,
  Search,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { StatTile } from '@/components/ui/StatTile';
import { Badge } from '@/components/ui/Badge';
import { Loader } from '@/components/ui/Loader';
import { fetchQueueStatus } from '@/services/api';
import type { QueueAgent, QueueStatus } from '@/types';
import { showErrorToast } from '@/utils/toast';
import {
  PROBLEM_STATES,
  STATE_META,
  STATE_ORDER,
  agentKey,
  agentLabel,
  byText,
  countStates,
  isAgentOffline,
  membershipState,
  type MembershipState,
} from './queueAgentState';

type ProblemState = (typeof PROBLEM_STATES)[number];
type AttentionFilter = 'all' | 'no_answer' | ProblemState;

/** A queue nobody can answer: no member is signed in, reachable and off DND. */
function nobodyCanAnswer(queue: QueueStatus) {
  return !queue.agents.some((agent) => membershipState(agent) === 'signed_in');
}

function agentsIn(queue: QueueStatus, state: MembershipState) {
  return queue.agents
    .filter((agent) => membershipState(agent) === state)
    .sort((a, b) => byText(agentLabel(a), agentLabel(b)));
}

export function QueueMonitor() {
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [attention, setAttention] = useState<AttentionFilter>('all');
  const loadInFlight = useRef(false);

  const loadQueueStatus = useCallback(async () => {
    if (loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      const data = await fetchQueueStatus();
      setQueues(data);
    } catch (error) {
      console.error('Error loading queue status:', error);
      showErrorToast(error, 'Failed to load queue status', 'queue-status-load-error');
    } finally {
      loadInFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueueStatus();

    // Auto-refresh every 3 seconds if enabled
    let interval: ReturnType<typeof setInterval> | undefined;
    if (autoRefresh) {
      interval = setInterval(loadQueueStatus, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, loadQueueStatus]);

  const getAgentStatusBadge = (agent: QueueAgent) => {
    const state = membershipState(agent);
    if (state === 'logged_out' || state === 'offline' || state === 'dnd') {
      return (
        <span
          title={STATE_META[state].description}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATE_META[state].chip}`}
        >
          {state === 'offline' ? 'Offline · no device' : STATE_META[state].label}
        </span>
      );
    }
    if (state === 'paused') {
      return (
        <Badge variant="warning">
          Paused{agent.pause_reason ? ` · ${agent.pause_reason}` : ''}
        </Badge>
      );
    }
    switch (agent.status) {
      case 'idle':
        return <Badge variant="success">Available</Badge>;
      case 'busy':
        return <Badge variant="danger">On Call</Badge>;
      case 'ringing':
        return <Badge variant="warning">Ringing</Badge>;
      default:
        return <Badge variant="default">Unavailable</Badge>;
    }
  };

  const totalWaiting = queues.reduce((sum, q) => sum + q.waiting_count, 0);
  const totalActive = queues.reduce((sum, q) => sum + q.active_count, 0);
  // Unique agents per state; an agent counts once even if in several queues.
  const allAgents = new Set<string>();
  const onlineAgentKeys = new Set<string>();
  const availableAgentKeys = new Set<string>();
  const problemAgents = Object.fromEntries(
    PROBLEM_STATES.map((state) => [state, new Set<string>()])
  ) as Record<ProblemState, Set<string>>;
  queues.forEach((queue) => {
    queue.agents.forEach((agent) => {
      const key = agentKey(agent);
      if (!key) return;
      allAgents.add(key);
      if (!isAgentOffline(agent)) onlineAgentKeys.add(key);
      const state = membershipState(agent);
      if (state === 'signed_in') {
        if (agent.status === 'idle') availableAgentKeys.add(key);
      } else {
        problemAgents[state].add(key);
      }
    });
  });
  const totalAgents = allAgents.size;
  const onlineAgents = onlineAgentKeys.size;
  const availableAgents = availableAgentKeys.size;
  const unansweredQueues = queues
    .filter(nobodyCanAnswer)
    .sort((a, b) => byText(a.queue_name || '', b.queue_name || ''));

  // Search matches queue name/number or any member's name/extension.
  const searchedQueues = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return queues;
    return queues.filter(
      (queue) =>
        queue.queue_name?.toLowerCase().includes(q) ||
        queue.queue_number?.toLowerCase().includes(q) ||
        queue.agents.some(
          (agent) =>
            agent.agent_name?.toLowerCase().includes(q) ||
            agent.agent_num?.toLowerCase().includes(q)
        )
    );
  }, [queues, search]);

  const queuesWith = (state: MembershipState) =>
    searchedQueues.filter((queue) =>
      queue.agents.some((a) => membershipState(a) === state)
    );

  const visibleQueues =
    attention === 'all'
      ? searchedQueues
      : attention === 'no_answer'
        ? searchedQueues.filter(nobodyCanAnswer)
        : queuesWith(attention);

  if (loading) {
    return (
      <Card>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
          Queue Monitor
        </h2>
        <Loader text="Loading queue status..." />
      </Card>
    );
  }

  const activeQueue = selectedQueue
    ? queues.find((q) => q.queue_id === selectedQueue)
    : null;

  const attentionOptions: Array<{
    id: AttentionFilter;
    label: string;
    count: number;
    dot?: string;
  }> = [
    { id: 'all', label: 'All queues', count: searchedQueues.length },
    {
      id: 'no_answer',
      label: 'Nobody can answer',
      count: searchedQueues.filter(nobodyCanAnswer).length,
      dot: 'bg-red-600',
    },
    ...PROBLEM_STATES.map((state) => ({
      id: state,
      label: `${STATE_META[state].label} agents`,
      count: queuesWith(state).length,
      dot: STATE_META[state].dot,
    })),
  ];

  const toggleAttention = (filter: AttentionFilter) => {
    setSelectedQueue(null);
    setAttention((current) => (current === filter ? 'all' : filter));
  };

  return (
    <Card>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Queue Monitor
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Real-time call queue monitoring
          </p>
        </div>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={`px-3 py-1.5 text-xs font-medium rounded-full ${
            autoRefresh
              ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
              : 'bg-panel text-gray-600 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
        </button>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <StatTile
          size="md"
          surface="inset"
          label="Waiting"
          icon={Clock}
          dotClassName={totalWaiting > 0 ? 'bg-amber-500' : undefined}
          value={totalWaiting}
          caption="Callers in a queue"
        />
        <StatTile size="md" surface="inset" label="Active" icon={Phone} value={totalActive} caption="Queue calls in progress" />
        <StatTile
          size="md"
          surface="inset"
          label="Agents online"
          icon={Users}
          value={onlineAgents}
          caption={`${totalAgents} total · ${totalAgents - onlineAgents} offline`}
        />
        <StatTile
          size="md"
          surface="inset"
          label="Agents available"
          icon={UserCheck}
          dotClassName="bg-primary-500"
          value={availableAgents}
          caption="Idle, reachable & not paused or DND"
        />
      </div>

      {/* Why agents won't get queue calls; each tile filters the queue list */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {PROBLEM_STATES.map((state) => {
          const meta = STATE_META[state];
          return (
            <StatTile
              key={state}
              size="md"
              surface="inset"
              label={`Agents ${meta.label.toLowerCase()}`}
              icon={null}
              dotClassName={meta.dot}
              value={problemAgents[state].size}
              caption={meta.description}
              active={attention === state}
              onClick={() => toggleAttention(state)}
              title={`Show queues with ${meta.label.toLowerCase()} agents`}
            />
          );
        })}
      </div>

      {unansweredQueues.length > 0 && (
        <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle
            size={20}
            className="text-red-600 dark:text-red-400 shrink-0 mt-0.5"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-red-800 dark:text-red-200">
              {unansweredQueues.length} queue
              {unansweredQueues.length === 1 ? ' has' : 's have'} nobody who can take
              calls
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Every member is offline, on DND, paused or logged out.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {unansweredQueues.map((queue) => (
                <button
                  key={queue.queue_id}
                  type="button"
                  onClick={() => setSelectedQueue(queue.queue_id)}
                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-200 dark:hover:bg-red-900"
                >
                  {queue.queue_name}
                  {queue.agents.length === 0 ? ' (no members)' : ''}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search and attention filters */}
      {queues.length > 0 && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center mb-4">
          <div className="relative flex-1 min-w-0">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedQueue(null);
              }}
              placeholder="Search queues by name or number, or find an agent…"
              className="w-full pl-9 pr-9 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {attentionOptions.map((option) => {
              const active = attention === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setSelectedQueue(null);
                    setAttention(option.id);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.dot && (
                    <span className={`w-2 h-2 rounded-full ${option.dot}`} />
                  )}
                  {option.label}
                  <span
                    className={
                      active ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                    }
                  >
                    {option.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Queue Details or Overview */}
      {activeQueue ? (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setSelectedQueue(null)}
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={14} />
            All queues
          </button>
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-3">
              {activeQueue.queue_name}
              {activeQueue.queue_number && (
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  {activeQueue.queue_number}
                </span>
              )}
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Waiting Calls</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {activeQueue.waiting_count}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Calls</p>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {activeQueue.active_count}
                </p>
              </div>
            </div>

            {activeQueue.agents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No agents</p>
            ) : (
              <div className="space-y-4">
                {STATE_ORDER.map((state) => {
                  const agents = agentsIn(activeQueue, state);
                  if (agents.length === 0) return null;
                  const meta = STATE_META[state];
                  const Icon = meta.icon;
                  return (
                    <div key={state}>
                      <h4
                        className={`flex items-center gap-1.5 text-sm font-semibold mb-2 ${meta.text}`}
                      >
                        <Icon size={14} />
                        {meta.label} ({agents.length})
                      </h4>
                      <div className="space-y-2">
                        {agents.map((agent, index) => (
                          <motion.div
                            key={agent.agent_id || agent.agent_num}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: Math.min(index, 10) * 0.03 }}
                            className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded"
                          >
                            <div className="flex items-center space-x-2">
                              <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {agent.agent_name || `Agent ${agent.agent_num}`}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Ext {agent.agent_num}
                                  {agent.agent_type === 'static' ? ' · Static' : ''}
                                </p>
                              </div>
                            </div>
                            {getAgentStatusBadge(agent)}
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : visibleQueues.length > 0 ? (
        <div className="space-y-3">
          {visibleQueues.map((queue, index) => {
            const counts = countStates(
              queue.agents.map((agent) => ({ state: membershipState(agent) }))
            );
            const noAnswer = nobodyCanAnswer(queue);
            const needsAttention = PROBLEM_STATES.some((state) => counts[state] > 0);
            return (
              <motion.div
                key={queue.queue_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index, 10) * 0.03 }}
                className={`bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-l-4 ${
                  noAnswer
                    ? 'border-red-600'
                    : needsAttention
                      ? 'border-amber-500'
                      : 'border-transparent'
                }`}
                onClick={() => setSelectedQueue(queue.queue_id)}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {queue.queue_name}
                      {queue.queue_number && (
                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                          {queue.queue_number}
                        </span>
                      )}
                    </h4>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span
                        className={`text-sm mr-1 ${
                          noAnswer
                            ? 'font-semibold text-red-700 dark:text-red-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {noAnswer
                          ? 'Nobody can take calls'
                          : `${counts.signed_in} of ${queue.agents.length} can take calls`}
                      </span>
                      {PROBLEM_STATES.filter((state) => counts[state] > 0).map(
                        (state) => (
                          <span
                            key={state}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATE_META[state].chip}`}
                          >
                            {counts[state]} {STATE_META[state].label.toLowerCase()}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Waiting</p>
                      <p className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
                        {queue.waiting_count}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Active</p>
                      <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                        {queue.active_count}
                      </p>
                    </div>
                  </div>
                </div>
                {needsAttention && (
                  <div className="mt-3 space-y-1 text-sm">
                    {PROBLEM_STATES.map(
                      (state) => [state, agentsIn(queue, state)] as const
                    ).map(([state, agents]) =>
                      agents.length === 0 ? null : (
                        <div
                          key={state}
                          className="flex flex-wrap items-baseline gap-1.5"
                        >
                          <span
                            className={`text-xs font-semibold ${STATE_META[state].text}`}
                          >
                            {STATE_META[state].label}:
                          </span>
                          {agents.map((agent) => (
                            <span
                              key={agentKey(agent)}
                              className={`px-2 py-0.5 rounded-full text-xs ${STATE_META[state].chip}`}
                            >
                              {agentLabel(agent)}
                              <span className="opacity-70"> · {agent.agent_num}</span>
                              {state === 'paused' && agent.pause_reason && (
                                <span className="opacity-70">
                                  {' '}
                                  · {agent.pause_reason}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      ) : queues.length > 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          {attention === 'all'
            ? 'No queues match your search'
            : attention === 'no_answer'
              ? 'Every queue has at least one agent who can take calls'
              : `No queues have ${STATE_META[attention].label.toLowerCase()} agents`}
        </p>
      ) : (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No queues found
        </p>
      )}
    </Card>
  );
}
